import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  OutlinedInput,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import { Refresh } from '@mui/icons-material';
import {
  DataGridPro,
  GridPagination,
  GridSortDirection,
} from '@mui/x-data-grid-pro';
import { ConfirmationModal } from '@components';
import { IPracticeTableEntry, usePracticeTable } from './hooks';
import { Select, TOOLBAR_FIELD_GAP, toolbarFieldSx } from 'ui';
import { TableNoResultPlaceholder, USStateSelect } from '@components';
import {
  PinnedColumnMenu,
  ColumnDensityFilter,
  TableColumnHeightMapper,
} from 'components/TablePreferences';
import { theme } from '@store';
import { useStableGridModels } from '@common/hooks';

interface IPracticeTableProps {
  rows: IPracticeTableEntry[];
  totalCount: number;
  onSelectPractice: (practiceId: string) => void;
  loading: boolean;
  selectedRowId: string;
}

export const PracticeTable = ({
  rows,
  totalCount,
  loading,
  onSelectPractice,
  selectedRowId,
}: IPracticeTableProps) => {
  const {
    apiRef,
    filters,
    columns,
    handleChangeOrder,
    onPageModelChange,
    handleSearchChange,
    partnerShortOptions,
    handleChangePartnerFilter,
    handleChangeStateFilter,
    handleDownload,
    isDownloading,
    searchInputValue,
    handleResetAll,
    initialSortFilters,
    isResetModalOpen,
    setIsResetModalOpen,
    pinnedColumns,
    handlePinnedColumnsChange,
    columnVisibilityModel,
    handleColumnVisibilityModelChange,
    handleColumnOrderChange,
    handleColumnWidthChange,
    density,
    handleDensityChange,
  } = usePracticeTable();

  const { paginationModel, sortModel, rowCount } = useStableGridModels({
    page: filters.page,
    limit: filters.limit,
    sort_by: filters.sort_by,
    sort_order: filters.sort_order,
    totalCount,
    loading,
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          bgcolor: 'white',
          padding: '1rem',
          border: theme => `1px solid ${theme.palette.grey[300]}`,
          borderBottom: 'none',
          gap: '1rem',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: TOOLBAR_FIELD_GAP,
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <OutlinedInput
            id='searchField'
            placeholder='Search practice'
            onChange={handleSearchChange}
            value={searchInputValue}
            sx={toolbarFieldSx}
            startAdornment={
              <InputAdornment sx={{ padding: 0 }} position='start'>
                <SearchIcon />
              </InputAdornment>
            }
          />

          <Select
            sx={{
              ...toolbarFieldSx,
              display: 'flex',
              justifyContent: 'center',
            }}
            name='partner'
            size='medium'
            label='Partner'
            value={filters.partner_id}
            onChange={handleChangePartnerFilter}
            options={partnerShortOptions}
            clearable
            clearLabel='All'
          />

          <USStateSelect
            name={'state'}
            value={filters.state}
            onChange={handleChangeStateFilter}
            clearable
            clearLabel='All'
            size='medium'
            sx={{
              ...toolbarFieldSx,
              display: 'flex',
              justifyContent: 'center',
            }}
          />
        </Box>

        <Box sx={{ display: 'flex' }}>
          <ColumnDensityFilter
            handleDensityChange={handleDensityChange}
            selectedValue={density}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            maxHeight: '40px',
            alignItems: 'center',
            width: '80px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              boxSizing: 'border-box',
              justifyContent: 'center',
              borderLeft: theme => `1px solid ${theme.palette.grey[300]}`,
            }}
          >
            <IconButton onClick={handleDownload} disabled={isDownloading}>
              <DownloadIcon sx={{ color: 'primary.main' }} />
            </IconButton>
          </Box>
        </Box>
      </Box>

      <DataGridPro
        apiRef={apiRef}
        sx={{
          maxHeight: 'calc(100vh - 410px)',
          '--DataGrid-overlayHeight': 'calc(100vh - 410px)',
          borderRadius: 0,
          boxShadow: 'none',
          border: theme => `1px solid ${theme.palette.grey[300]}`,
          borderBottom: 'none',
          '& .MuiDataGrid-cell': { alignItems: 'center', display: 'flex' },
          '& .selected-row': {
            bgcolor: theme.palette.selectedTableRowBg.main,
            '&:hover': { bgcolor: theme.palette.selectedTableRowBg.main },
          },
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
        slots={{
          columnMenu: props => (
            <PinnedColumnMenu {...props} pinnedColumns={pinnedColumns} />
          ),
          pagination: props => (
            <Box
              sx={{
                px: 2,
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Button
                color='primary'
                sx={{ whiteSpace: 'nowrap' }}
                startIcon={<Refresh color='primary' />}
                onClick={() => setIsResetModalOpen(true)}
              >
                RESET VIEW
              </Button>
              <GridPagination {...props} />
            </Box>
          ),
          noRowsOverlay: () => (
            <Box
              sx={{
                height: '100%',
                display: 'grid',
                placeItems: 'center',
                bgcolor: theme => theme.palette.common.white,
              }}
            >
              <TableNoResultPlaceholder />
            </Box>
          ),
        }}
        pinnedColumns={pinnedColumns}
        onPinnedColumnsChange={handlePinnedColumnsChange}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
        onColumnOrderChange={handleColumnOrderChange}
        onColumnWidthChange={handleColumnWidthChange}
        getRowHeight={() => TableColumnHeightMapper[density ?? 'comfortable']}
        pageSizeOptions={[10, 25, 50, 100]}
        pagination
        paginationModel={paginationModel}
        rows={rows}
        columns={columns}
        rowCount={rowCount}
        loading={loading}
        disableMultipleColumnsSorting
        disableRowSelectionOnClick
        paginationMode='server'
        sortingMode='server'
        disableColumnFilter
        disableColumnSelector
        disableColumnReorder
        sortModel={sortModel}
        onPaginationModelChange={onPageModelChange}
        onSortModelChange={model => {
          if (
            model[0] &&
            (model[0].field !== filters.sort_by ||
              model[0].sort !== filters.sort_order)
          ) {
            handleChangeOrder(model[0].field, model[0].sort);
          } else {
            handleChangeOrder(
              initialSortFilters.sort_by,
              initialSortFilters.sort_order as GridSortDirection,
            );
          }
        }}
        onRowClick={params => onSelectPractice(params.row.id)}
        getRowClassName={params => {
          const zebra =
            params.indexRelativeToCurrentPage % 2 !== 0 ? 'row-even' : '';
          const selected =
            params.row.id === selectedRowId ? 'selected-row' : '';
          return [zebra, selected].filter(Boolean).join(' ');
        }}
      />
      <ConfirmationModal
        title='Reset table view'
        message='This will restore the default columns, sorting, and filters. Your custom layout and pinned columns will be cleared.'
        closeText='KEEP CURRENT VIEW'
        confirmText='RESET VIEW'
        onConfirm={() => {
          handleResetAll();
          setIsResetModalOpen(false);
        }}
        onClose={() => setIsResetModalOpen(false)}
        isOpen={isResetModalOpen}
        reverseButtonOrder
      />
    </Box>
  );
};
